import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, defer, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, finalize, map, switchMap, take, tap, timeout } from 'rxjs/operators';
import { DwCacheService, DwConfigService, DwConfigServiceToken, DwEventService, DwOrmDataService,
    DwSecurityTopics, DwSecurityUserService, SuppressErrorMessagesHeader } from '@devwareapps/devware-cap';
import { CartCommand, CartItemInput, CartRecord, CheckoutConfiguration, CheckoutResult, CommerceProduct,
    DeliveryInput, EnrollmentStatus, OrderConfirmation } from '../models/commerce.models';

import { OrderHistorySummary, OrderHistoryDetail } from '../models/order-history.models';

interface PendingCommand { method: string; path: string; body: any; key: string; scope: string; checkout: boolean; }

@Injectable({ providedIn: 'root' })
export class FitnessCommerceService {
    private readonly handledErrorHeaders = { [SuppressErrorMessagesHeader]: 'true' };
    private state = new BehaviorSubject<CartRecord>(null);
    readonly cart$ = this.state.asObservable();
    // Reading a saved draft must never create an order or add an item.
    readonly resumableOrder$ = this.cart$.pipe(map(current => {
        let saved = current;
        if (!saved) {
            try { saved = JSON.parse(window.localStorage.getItem(this.storageKey()) || 'null'); } catch (_) {}
        }
        return saved?.cart?.lines?.length &&
            ((saved.state === 'Cart' && Date.parse(saved.expiresUtc) > Date.now()) || saved.state === 'Pending') ? saved : null;
    }));
    private accountChanged = new Subject<void>();
    readonly accountChanged$ = this.accountChanged.asObservable();
    private pending: PendingCommand;
    private executing = false;
    private revision = 0;
    get cart(): CartRecord { return this.state.value; }
    get retryRequired(): boolean { return !!this.pending; }
    get busy(): boolean { return this.executing; }
    forgetDeletedOrders(orderIds: number[]): void {
        let saved: any;
        try { saved=JSON.parse(window.localStorage.getItem(this.storageKey()) || 'null'); } catch (_) {}
        if (saved && orderIds.includes(saved.orderId)) { try { window.localStorage.removeItem(this.storageKey()); } catch (_) {} }
        if (this.cart && orderIds.includes(this.cart.orderId)) { this.pending=null; this.state.next(null); }
        this.events.publishEvent('Fitness.ReferenceData.Refresh', {});
    }

    constructor(private http: HttpClient, @Inject(DwConfigServiceToken) private config: DwConfigService,
        private cache: DwCacheService, private security: DwSecurityUserService, private events: DwEventService) {
        this.security.securityContextChanged().subscribe(() => {
            this.revision++; this.pending = null; this.executing = false; this.state.next(null);
            this.accountChanged.next();
        });
    }
    private scope(): string {
        const c = this.security.securityContext;
        return JSON.stringify([this.config.coreConfig.apiRoot, c && c.ApiRoot,
            c && c.ApplicationUser && c.ApplicationUser.UserId, c && c.AuthenticatedUser && c.AuthenticatedUser.UserId, this.revision]);
    }
    private url(path: string): string { return this.config.coreConfig.apiRoot.replace(/\/$/, '') + '/fitness/' + path; }
    private response<T>(value: any): T {
        // CAP hosts may retain PascalCase, including dictionary keys in enrollment status.
        if (Array.isArray(value)) { return value.map(v => this.response(v)) as any; }
        if (value && typeof value === 'object') {
            const result = {}; Object.keys(value).forEach(key =>
                result[key.charAt(0).toLowerCase() + key.slice(1)] = this.response(value[key])); return result as T;
        }
        return value;
    }
    private assertScope(scope: string): void {
        if (scope !== this.scope()) { throw new Error('Your account changed. Reload checkout before continuing.'); }
    }
    products(): Observable<CommerceProduct[]> {
        return this.reference<CommerceProduct[]>('products', ['dw_Product', 'dw_ProductSize', 'dw_ProductColor', 'dw_ProductOption', 'dw_ProductOptionDetail', 'dw_ProductType']);
    }
    configuration(): Observable<CheckoutConfiguration> {
        return this.reference<CheckoutConfiguration>('checkout/configuration', ['dw_Country', 'dw_StateProvince', 'dw_ShippingRate', 'UserProfile', 'Address']);
    }
    lookupParticipant(email: string, challengeDefinitionId?: number): Observable<{ found: boolean; alreadyRegistered?: boolean; firstName?: string; lastName?: string; previousStreaker?: boolean; yearsStreakingCv?: number }> {
        return defer(() => {
            const scope = this.scope();
            return this.http.post(this.url('registrations/participant-lookup'), { email, challengeDefinitionId }, { headers: this.handledErrorHeaders })
                .pipe(take(1), map(v => this.response<any>(v)), tap(() => this.assertScope(scope)));
        });
    }
    private reference<T>(path: string, items: string[]): Observable<T> {
        return defer(() => {
            const scope = this.scope();
            return this.cache.getWithOptions<T>('Fitness.Commerce.' + scope + '.' + path,
                this.http.get<T>(this.url(path), { headers: this.handledErrorHeaders }).pipe(take(1), map(v => this.response<T>(v))), {
                    cacheInvalidationsTopics: [DwSecurityTopics.SECURITY_CHANGED, 'Fitness.ReferenceData.Refresh',
                        ...items.map(item => DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(item))],
                }).pipe(take(1), tap(() => this.assertScope(scope)));
        });
    }
    private storageKey(): string {
        const c = this.security.securityContext;
        return 'Fitness.Cart.v2.' + JSON.stringify([this.config.coreConfig.apiRoot, c && c.ApiRoot,
            c && c.ApplicationUser && c.ApplicationUser.UserId, c && c.AuthenticatedUser && c.AuthenticatedUser.UserId]);
    }
    registrationAttribution(referralCode?: string, teamCode?: string): { referralCode: string; teamCode: string } {
        const key = this.storageKey() + '.Attribution';
        let saved = { referralCode: '', teamCode: '' };
        try { saved = JSON.parse(window.sessionStorage.getItem(key) || 'null') || saved; } catch (_) {}
        const result = { referralCode: referralCode == null ? saved.referralCode || '' : referralCode.trim(),
            teamCode: teamCode == null ? saved.teamCode || '' : teamCode.trim() };
        try { window.sessionStorage.setItem(key, JSON.stringify(result)); } catch (_) {}
        return result;
    }
    private remember(cart: CartRecord): void {
        // Paid orders retain only an owned order pointer, not participant/address data.
        const saved = cart && cart.orderId > 0 ? { orderId: cart.orderId } : cart;
        try { window.localStorage.setItem(this.storageKey(), JSON.stringify(saved)); } catch (_) { /* Keep the in-memory cart when browser storage is unavailable. */ }
        this.state.next(cart);
    }
    restore(orderId?: number): Observable<CartRecord> {
        return defer(() => {
            const scope = this.scope();
            let saved: CartRecord;
            try { saved = JSON.parse(window.localStorage.getItem(this.storageKey()) || 'null'); }
            catch (_) { saved = null; }
            const localDraft = this.cart && this.cart.state === 'Cart' ? this.cart : saved;
            const id = orderId || saved && saved.orderId;
            return this.http.get<CartRecord>(this.url(id ? 'orders/' + id : 'carts/current'), { headers: this.handledErrorHeaders }).pipe(
                catchError(error => {
                    // An admin may have removed this test order from another browser.
                    // Only discard an implicit saved pointer after a confirmed 404.
                    if (error.status !== 404 || orderId || !id) { return throwError(error); }
                    this.assertScope(scope);
                    try { window.localStorage.removeItem(this.storageKey()); } catch (_) {}
                    return this.http.get<CartRecord>(this.url('carts/current'), { headers: this.handledErrorHeaders });
                }),
                take(1), map(v => this.response<CartRecord>(v)), switchMap(current => {
                    // A cancelled saved pointer must not trap the store in an immutable order.
                    // Explicit order links can still show the cancellation confirmation.
                    if (current?.state !== 'Cancelled' || orderId) { return of(current); }
                    this.assertScope(scope);
                    return this.http.get<CartRecord>(this.url('carts/current'), { headers: this.handledErrorHeaders }).pipe(
                        take(1), map(v => this.response<CartRecord>(v)),
                        switchMap(pending => { this.assertScope(scope); return pending ? of(pending) : this.create(); }));
                }), map(current => {
                    if (current || id) { return current; }
                    if (!localDraft || !localDraft.draftId || !localDraft.cart || Date.parse(localDraft.expiresUtc) <= Date.now()) { return null; }
                    return { ...localDraft, cart: { ...localDraft.cart, quote: null } };
                }), tap(cart => { this.assertScope(scope); if (cart) { this.remember(cart); } else { this.state.next(null); } }));
        });
    }
    loadForCheckout(orderId?: number): Observable<CartRecord> {
        return defer(() => {
            // Navigation within this account can use the current browser draft.
            // Reloads and saved payment orders still read the server; payment
            // always validates the submitted draft and quote again.
            if (!orderId && this.cart?.state === 'Cart' && !this.retryRequired && !this.busy &&
                Date.parse(this.cart.expiresUtc) > Date.now()) return of(this.cart);
            return this.restore(orderId);
        });
    }
    private draft(value: Partial<CartCommand> = {}): CartCommand {
        const c = this.requireCart();
        // The new address must also replace a saved/partial address inside the draft:
        // ASP.NET validates the entire nested request before the action runs.
        return { ...value, expectedVersion: c.version, draftId: c.draftId,
            draft: value.delivery ? { ...c.cart, delivery: value.delivery } : c.cart };
    }
    create(): Observable<CartRecord> {
        if (this.cart && this.cart.state === 'Pending') { return throwError(new Error('Resolve the existing payment before starting another order.')); }
        return this.command<CartRecord>('POST', 'carts', {});
    }
    saveItem(item: CartItemInput, itemId?: string): Observable<CartRecord> {
        const c = this.requireCart();
        return this.command<CartRecord>(itemId ? 'PATCH' : 'POST', 'carts/' + c.orderId + '/items' +
            (itemId ? '/' + encodeURIComponent(itemId) : ''), this.draft({ item }));
    }
    remove(itemId: string): Observable<CartRecord> {
        const c = this.requireCart();
        return this.command<CartRecord>('POST', 'carts/' + c.orderId + '/items/' + encodeURIComponent(itemId) + '/remove', this.draft());
    }
    delivery(delivery: DeliveryInput): Observable<CartRecord> {
        const address = (a: DeliveryInput['billingAddress']) => a ? { ...a,
            countryId: +a.countryId, stateProvinceId: a.stateProvinceId ? +a.stateProvinceId : null } : a;
        return this.mutate('delivery', { delivery: { ...delivery,
            billingAddress: address(delivery.billingAddress),
            shippingAddress: delivery.sameAsBilling ? null : address(delivery.shippingAddress) } });
    }
    promotion(promotionCode: string): Observable<CartRecord> { return this.mutate('promotion', { promotionCode }); }
    quote(): Observable<CartRecord> { return this.mutate('quote', {}); }
    /** Server-calculated preview; no cart/order/payment is saved or published. */
    previewQuote(delivery: DeliveryInput): Observable<CartRecord> {
        return defer(() => {
            const cart = this.requireCart(), scope = this.scope();
            if (cart.state !== 'Cart') { return throwError(new Error('Only a draft can be repriced.')); }
            return this.http.post(this.url('carts/0/quote'), this.draft({ delivery }), { headers: this.handledErrorHeaders }).pipe(
                timeout(30000), take(1), map(value => this.response<CartRecord>(value)), tap(() => this.assertScope(scope)));
        });
    }
    private mutate(operation: string, value: Partial<CartCommand>): Observable<CartRecord> {
        const c = this.requireCart();
        return this.command<CartRecord>(operation === 'quote' ? 'POST' : 'PUT', 'carts/' + c.orderId + '/' + operation,
            this.draft(value));
    }
    checkout(paymentProvider?: 'paypal-legacy'): Observable<CheckoutResult> {
        const c = this.requireCart(), quote = c.cart.quote;
        if (!quote || quote.total == null || quote.issues.length || Date.parse(quote.expiresUtc) <= Date.now()) {
            return throwError(new Error('Refresh and review the quote before payment.'));
        }
        return this.command<CheckoutResult>('POST', 'carts/' + c.orderId + '/checkout',
            this.draft({ quoteId: quote.quoteId, paymentProvider }), true);
    }
    legacyPayPalCard(card: { name: string; number: string; expiry: string; cvc: string }): Observable<CheckoutResult> {
        // Card data must never enter the retained-command queue or browser storage.
        return defer(() => {
            const c = this.requireCart(), scope = this.scope();
            return this.http.post<CheckoutResult>(this.url('orders/' + c.orderId + '/paypal-legacy/card'), card,
                { headers: this.handledErrorHeaders }).pipe(take(1), map(v => this.response<CheckoutResult>(v)),
                tap(() => this.assertScope(scope)), switchMap(result => this.restore(c.orderId).pipe(map(() => result))),
                tap(result => { if (result.status === 'Paid') { this.events.publishEvent('Fitness.ReferenceData.Refresh', {}); } }));
        });
    }
    retry(): Observable<CartRecord | CheckoutResult> {
        if (!this.pending) { return throwError(new Error('There is no request to retry.')); }
        return this.send(this.pending);
    }
    cancelOrder(keepItems = false): Observable<CartRecord> {
        return defer(() => {
            const c = this.requireCart(), scope = this.scope();
            return this.http.post<CartRecord>(this.url('orders/' + c.orderId + '/cancel'), { keepItems },
                { headers: this.handledErrorHeaders }).pipe(take(1), map(v => this.response<CartRecord>(v)),
                tap(draft => { this.assertScope(scope); this.pending = null; this.remember(draft); }));
        });
    }
    checkPaymentStatus(): Observable<CheckoutResult> {
        return defer(() => {
            const c = this.requireCart(), scope = this.scope();
            return this.http.post<CheckoutResult>(this.url('orders/' + c.orderId + '/payment/status'), {},
                { headers: this.handledErrorHeaders }).pipe(take(1), map(v => this.response<CheckoutResult>(v)),
                tap(() => this.assertScope(scope)), switchMap(result => this.restore(c.orderId).pipe(map(() => result))));
        });
    }
    resumePayment(): Observable<CheckoutResult> {
        const c = this.requireCart(), scope = this.scope();
        return this.http.post<CheckoutResult>(this.url('orders/' + c.orderId + '/payment'), {}, { headers: this.handledErrorHeaders }).pipe(take(1),
            map(v => this.response<CheckoutResult>(v)), tap(() => this.assertScope(scope)),
            switchMap(result => this.restore(c.orderId).pipe(map(() => result))));
    }
    capture(): Observable<CheckoutResult> {
        return defer(() => {
            const c = this.requireCart(), scope = this.scope();
            // Server capture is idempotent and only uses its stored provider order.
            return this.http.post<CheckoutResult>(this.url('orders/' + c.orderId + '/paypal/capture'), {}, { headers: this.handledErrorHeaders }).pipe(
                take(1), map(v => this.response<CheckoutResult>(v)), tap(() => this.assertScope(scope)),
                switchMap(result => this.restore(c.orderId).pipe(map(() => result))),
                tap(result => { if (result.status === 'Paid') { this.events.publishEvent('Fitness.ReferenceData.Refresh', {}); } }));
        });
    }
    orders(showAll = false): Observable<OrderHistorySummary[]> {
        return this.readOrderHistory<OrderHistorySummary[]>('orders?showAll=' + showAll);
    }
    orderDetails(orderId: number): Observable<OrderHistoryDetail> {
        return this.readOrderHistory<OrderHistoryDetail>('orders/' + orderId + '/details');
    }
    private readOrderHistory<T>(path: string): Observable<T> {
        return defer(() => {
            const scope = this.scope();
            return this.http.get(this.url(path), { headers: this.handledErrorHeaders }).pipe(take(1),
                map(value => this.response<T>(value)), tap(() => this.assertScope(scope)));
        });
    }
    confirmation(orderId: number): Observable<OrderConfirmation> {
        return defer(() => {
            const scope = this.scope();
            return this.http.get(this.url('orders/' + orderId + '/confirmation'), { headers: this.handledErrorHeaders })
                .pipe(take(1), map(v => this.response<OrderConfirmation>(v)), tap(() => this.assertScope(scope)));
        });
    }
    retryRegistrationSetup(orderId: number): Observable<OrderConfirmation> {
        return defer(() => {
            const scope = this.scope();
            return this.http.post(this.url('orders/' + orderId + '/registrations/retry'), {}, { headers: this.handledErrorHeaders })
                .pipe(take(1), map(v => this.response<OrderConfirmation>(v)), tap(() => this.assertScope(scope)));
        });
    }
    enrollments(): Observable<EnrollmentStatus[]> {
        const c = this.requireCart(), scope = this.scope();
        return this.http.get<EnrollmentStatus[]>(this.url('orders/' + c.orderId + '/registrations'), { headers: this.handledErrorHeaders })
            .pipe(take(1), map(v => this.response<EnrollmentStatus[]>(v)), tap(() => this.assertScope(scope)));
    }
    private requireCart(): CartRecord {
        if (!this.cart) { throw new Error('Load or create a cart first.'); } return this.cart;
    }
    private command<T>(method: string, path: string, body: any, checkout = false): Observable<T> {
        return defer(() => {
            if (this.pending || this.executing) { return throwError(new Error('Retry the retained request before making another change.')); }
            const bytes = new Uint8Array(16); window.crypto.getRandomValues(bytes);
            const pending: PendingCommand = { method, path, body: JSON.parse(JSON.stringify(body)),
                key: Array.from(bytes).map(b => ('0' + b.toString(16)).slice(-2)).join(''), scope: this.scope(), checkout };
            this.pending = pending;
            return this.send<T>(pending);
        });
    }
    private send<T>(pending: PendingCommand): Observable<T> {
        return defer(() => {
            this.assertScope(pending.scope);
            if (this.executing) { return throwError(new Error('A cart request is already in progress.')); }
            this.executing = true;
            return this.http.request<T>(pending.method, this.url(pending.path), {
                body: pending.body, headers: { ...this.handledErrorHeaders, 'Idempotency-Key': pending.key },
            }).pipe(take(1), map(v => this.response<T>(v)), tap(result => {
                this.assertScope(pending.scope); this.pending = null; this.executing = false;
                if (!pending.checkout) { this.remember(result as any); }
                else {
                    const payment = result as any;
                    this.remember({ ...this.cart, orderId: payment.orderId, state: payment.status, payPalOrderId: payment.payPalOrderId });
                }
                if (pending.checkout && (result as any).status === 'Paid') { this.events.publishEvent('Fitness.ReferenceData.Refresh', {}); }
            }), catchError(error => {
                if (pending.scope === this.scope()) {
                    this.executing = false;
                    // Preserve exact input/key on network and server errors: commit outcome can be unknown.
                    if ((error.status >= 400 && error.status < 500) || error.error && error.error.code === 'commerce_disabled') { this.pending = null; }
                }
                return throwError(error);
            }), finalize(() => {
                if (pending.scope === this.scope() && this.pending === pending) { this.executing = false; }
            }));
        });
    }
}


